import React, { useRef, useState, useContext } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import { Link, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const AttendanceApiPage = () => {
  const { roles } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null); // State for file preview
  // Define state for year and day
  const [yearDay, setYearDay] = useState(`${new Date().getFullYear()}-01`);
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [error, setError] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [uploadResponse, setUploadResponse] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [monthlyRecords, setMonthlyRecords] = useState(null);
  const [employeeRecords, setEmployeeRecords] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentStep, setCurrentStep] = useState("upload"); // 'upload', 'preview', 'confirmed'
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 30,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);

  // console.log("pagination", pagination)

  // Derived flags from preview
  const hasBadTimestamps = Boolean(
    (previewData?.details?.lines_bad_ts &&
      previewData.details.lines_bad_ts.length > 0) ||
    (previewData?.counts?.lines_bad_ts && previewData.counts.lines_bad_ts > 0),
  );
  const hasPreviewRows =
    Array.isArray(previewData?.preview_rows) &&
    previewData.preview_rows.length > 0;

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  const processSelectedFile = (selectedFile) => {
    if (!selectedFile) return;
    const isTxt = selectedFile.name.endsWith(".txt");
    const isSizeValid = selectedFile.size <= 5 * 1024 * 1024; // 5MB in bytes

    if (!isTxt) {
      alert("Only .txt files are allowed.");
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    if (!isSizeValid) {
      alert("File size must be less than or equal to 5MB.");
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setFilePreview(content.split("\n")); // Preview all lines
    };
    reader.readAsText(selectedFile);

    setFile(selectedFile);
    setCurrentStep("upload");
    setPreviewData(null);
    setUploadId(null);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files && e.target.files[0];
    processSelectedFile(selected);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile =
      e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    processSelectedFile(droppedFile);
  };

  // Handler to update year
  const handleYearChange = (e) => {
    const newYear = e.target.value;
    const [, day] = yearDay.split("-"); // Extract current day
    setYearDay(`${newYear}-${day}`);
  };

  // Handler to update day
  const handleDayChange = (e) => {
    const newDay = e.target.value;
    const [year] = yearDay.split("-"); // Extract current year
    setYearDay(`${year}-${newDay}`);
  };

  const uploadAttendance = async () => {
    setLoadingPreview(true);
    if (!file)
      return (
        alert("Please select a file to upload."),
        setLoadingPreview(false)
      );
    const formData = new FormData();
    // console.log(file)
    formData.append("attendance_file", file);

    try {
      const response = await axios.post(
        "https://hrmsnew.prepstripe.com/api/attendance-uploads/preview",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setLoadingPreview(false);
      setPreviewData(response.data);
      setUploadId(response.data.token); // Store token for confirmation
      setCurrentStep("preview");
      setNotification({
        message: `${response.data.summary_message || "File processed successfully. Please review the data below."}`,
        type: "success",
      });
    } catch (error) {
      setLoadingPreview(false);
      console.error("Error processing attendance file:", error);
      setNotification({
        message:
          error.response?.data?.message ||
          "Failed to process attendance file. Please try again.",
        type: "error",
      });
    }
  };

  const confirmUpload = async () => {
    if (!uploadId) {
      setNotification({
        message: "No upload to confirm. Please upload a file first.",
        type: "error",
      });
      return;
    }

    setLoadingConfirm(true);
    try {
      const response = await axios.post(
        "https://hrmsnew.prepstripe.com/api/attendance-uploads/confirm",
        {
          token: uploadId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setLoadingConfirm(false);
      setUploadResponse(response.data);
      setCurrentStep("confirmed");
      setNotification({
        message: "Attendance data confirmed and saved successfully!",
        type: "success",
      });

      // Reset file input and states after successful confirmation
      // resetFileSelection();
      // Navigate to check attendance page after a brief delay
      // setTimeout(() => {
      //     navigate(getRoleBasedRoute("/admin/attendance_management/check-attendance/", roles));
      // }, 4000);
    } catch (error) {
      setLoadingConfirm(false);
      console.error("Error confirming attendance upload:", error);
      setNotification({
        message:
          error.response?.data?.message ||
          "Failed to confirm attendance upload. Please try again.",
        type: "error",
      });
    }
  };

  const fetchMonthlyAttendance = async (page = 1) => {
    setLoadingEmployee(true);
    setError(null); // Reset error state before fetching

    try {
      const response = await axios.post(
        `https://hrmsnew.prepstripe.com/api/attendance/monthly?page=${page}`,
        { period: yearDay },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setMonthlyRecords(response.data);
      setPagination({
        currentPage: response.data?.current_page || 1,
        totalPages: response.data?.last_page || 1,
        totalItems: response.data?.total || 0,
        perPage: response.data?.per_page || 30,
      });
    } catch (error) {
      console.error("Error fetching monthly attendance:", error);
      setError("Failed to fetch monthly attendance. Please try again.");
    } finally {
      setLoadingEmployee(false);
    }
  };

  const formatTo12Hour = (timeStr) => {
    if (!timeStr) return ""; // return empty string if null/undefined

    const date = new Date(`1970-01-01T${timeStr}`); // convert to date assuming only time
    const options = { hour: "numeric", minute: "numeric", hour12: true };
    return date.toLocaleTimeString("en-US", options);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchMonthlyAttendance(newPage);
    }
  };

  const resetFileSelection = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // clear file input
    }
    setFile(null); // reset file state
    setFilePreview(null); // reset preview
    setUploadResponse(null); // optional: clear upload response
    setPreviewData(null); // clear preview data
    setUploadId(null); // clear upload ID
    setCurrentStep("upload"); // reset to upload step
  };

  const cancelPreview = () => {
    setCurrentStep("upload");
    setPreviewData(null);
    setUploadId(null);
  };

  const handleDownload = async () => {
    try {
      // Fetch file from public folder (relative URL)
      const response = await fetch("/hrmsAttend.txt");
      const blob = await response.blob();

      // Create temp download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "hrmsAttend_sample.txt";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    // mobile responsive code
    <div className="px-4 py-2 bg-white dark:bg-gray-900">
      {/* Breadcrumb */}
      <nav className="mb-4 font-poppins" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center space-x-1 md:space-x-2 rtl:space-x-reverse text-sm">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page" className="flex items-center">
            <svg
              className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
              fill="none"
              viewBox="0 0 6 10"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 9 4-4-4-4"
              />
            </svg>
            <span className="text-gray-500">Upload Attendance</span>
          </li>
        </ol>
      </nav>

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Progress Steps (2-step: Upload -> Preview) */}
      <div className="mb-6 bg-white dark:bg-black p-4 font-poppins shadow-md rounded-lg">
        <div className="flex items-center justify-center space-x-4 mb-4">
          <div
            className={`flex items-center ${currentStep === "upload" ? "text-blue-600" : "text-green-600"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${currentStep === "upload" ? "bg-blue-600" : "bg-green-600"}`}
            >
              1
            </div>
            <span className="ml-2 text-sm font-medium">Upload</span>
          </div>
          <div
            className={`h-0.5 w-16 ${currentStep === "upload" ? "bg-gray-300" : "bg-green-600"}`}
          ></div>
          <div
            className={`flex items-center ${currentStep === "upload" ? "text-gray-400" : "text-blue-600"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${currentStep === "upload" ? "bg-gray-400" : "bg-blue-600"}`}
            >
              2
            </div>
            <span className="ml-2 text-sm font-medium">Preview</span>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleDownload}
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Download Sample File
          </button>
        </div>
      </div>

      {/* File Upload Section */}
      {currentStep === "upload" && (
        <div className="mb-6 bg-white dark:bg-black p-4 font-poppins shadow-md rounded-lg">
          <h2 className="md:text-lg text-md font-semibold mb-2 dark:text-white">
            Upload Attendance File
          </h2>
          <input
            type="file"
            accept=".txt"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <div
            className={`mb-2 w-full cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors ${isDragOver ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20" : "border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"}`}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-gray-500 dark:text-gray-300 mb-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 3a1 1 0 011-1h5a1 1 0 01.707.293l6 6A1 1 0 0116 9h-3a2 2 0 00-2 2v3a1 1 0 11-2 0v-3a4 4 0 014-4h2.586L9 2.414V5a1 1 0 11-2 0V3H4a1 1 0 00-1 1v12a1 1 0 001 1h7a1 1 0 110 2H4a3 3 0 01-3-3V4a1 1 0 012 0z"
                  clipRule="evenodd"
                />
              </svg>
              {!file ? (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    Drag & drop your <span className="font-medium">.txt</span>{" "}
                    file here
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    or click to browse
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    Selected file:
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                    {file.name}
                  </p>
                </>
              )}
            </div>
          </div>
          {filePreview && (
            <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-md mb-4">
              <h3 className="text-sm font-semibold mb-2 dark:text-white">
                File Preview (All Rows):
              </h3>
              <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-auto max-h-40">
                {filePreview.map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </pre>
            </div>
          )}
          <div className="flex gap-4">
            {file && (
              <button
                onClick={uploadAttendance}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md w-full sm:w-auto"
                disabled={!file || loadingPreview}
              >
                {loadingPreview ? "Processing..." : "Process File"}
              </button>
            )}
            {file && (
              <button
                onClick={resetFileSelection}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md w-full sm:w-auto"
                disabled={loadingPreview}
              >
                Reset
              </button>
            )}
          </div>
          <p className="text-gray-500 text-xs mt-2 font-poppins">
            Only <span className="font-medium">.txt</span> files. Max size:{" "}
            <span className="font-medium">5MB</span>.
          </p>
        </div>
      )}

      {/* Preview Section */}
      {currentStep === "preview" && previewData && (
        <div className="mb-6 bg-white dark:bg-black p-4 font-poppins shadow-md rounded-lg">
          <h2 className="md:text-lg text-md font-semibold mb-4 dark:text-white">
            Preview Attendance Data
          </h2>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {previewData.counts?.unique_employees > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Unique Entries
                </h3>
                <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                  {previewData.counts?.unique_employees}
                </p>
              </div>
            )}
            {previewData.counts?.duplicate_days > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Duplicate Entries
                </h3>
                <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                  {previewData.counts?.duplicate_days}
                </p>
              </div>
            )}
            {previewData.counts?.skipped_emp_ids > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Skipped Employee IDs
                </h3>
                <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                  {previewData.counts?.skipped_emp_ids}
                </p>
              </div>
            )}
            {previewData.counts?.lines_bad_ts > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Bad Timestamp
                </h3>
                <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                  {previewData.counts?.lines_bad_ts}
                </p>
              </div>
            )}
            {previewData.counts?.lines_malformed > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Malformed Lines
                </h3>
                <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                  {previewData.counts?.lines_malformed}
                </p>
              </div>
            )}
          </div>

          {/* Additional Stats */}
          {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"> */}
          {/* <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Total Grouped Days</h3>
                            <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                                {previewData.counts?.total_grouped_days || 0}
                            </p>
                        </div> */}

          {/* </div> */}

          {/* Data Preview Table */}
          {/* {previewData.preview_rows && previewData.preview_rows.length > 0 ? (
                        <div className="overflow-x-auto mb-6">
                            <h3 className="text-sm font-semibold mb-2 dark:text-white">Unique Row Data:</h3>
                            <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Employee ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Check In</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Check Out</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                    {previewData.preview_rows.map((record, index) => (
                                        <tr key={index}>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {record.employee_id || '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {record.attendance_date ? new Date(record.attendance_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {record.in_time ? formatTo12Hour(record.in_time) : '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {record.out_time ? formatTo12Hour(record.out_time) : '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200">
                                                    Valid
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                            <p className="text-yellow-800 dark:text-yellow-200">
                                No preview rows available. 
                            </p>
                        </div>
                    )} */}

          {/* duplicate days Preview Table */}
          {/* {previewData.details.duplicate_days && previewData.details.duplicate_days.length > 0 ? (
                        <div className="overflow-x-auto mb-6">
                            <h3 className="text-sm font-semibold mb-2 dark:text-white">Duplicate Row Data:</h3>
                            <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Employee Date</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Check In</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Check Out</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                    {previewData.details.duplicate_days.map((record, index) => (
                                        <tr key={index}>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {record.employee_date || '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {record.existing_in_time ? formatTo12Hour(record.existing_in_time) : '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {record.existing_out_time ? formatTo12Hour(record.existing_out_time) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                            <p className="text-yellow-800 dark:text-yellow-200">
                                No preview rows available. 
                            </p>
                        </div>
                    )} */}

          {/* Issues and Details */}
          {/* {previewData.details?.lines_bad_ts && previewData.details.lines_bad_ts.length > 0 && (
                        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Lines with Bad Timestamps:</h3>
                            <div className="space-y-2">
                                {previewData.details.lines_bad_ts.map((error, index) => (
                                    <div key={index} className="text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 p-2 rounded">
                                        <strong>Line {error.line}:</strong> Date: {error.date}, Time: "{error.time}" (Invalid timestamp format)
                                    </div>
                                ))}
                            </div>
                        </div>
                    )} */}

          {/* Important Note */}
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
              📝 Important Note:
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {previewData.note}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            {hasPreviewRows && (
              <button
                onClick={confirmUpload}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-md flex-1 sm:flex-none"
                disabled={loadingConfirm}
              >
                {loadingConfirm ? "Confirming..." : "Confirm & Save Data"}
              </button>
            )}
            <button
              onClick={cancelPreview}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md flex-1 sm:flex-none"
              disabled={loadingConfirm}
            >
              Cancel
            </button>
            <button
              onClick={resetFileSelection}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-md flex-1 sm:flex-none"
              disabled={loadingConfirm}
            >
              Start Over
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Success */}
      {currentStep === "confirmed" && uploadResponse && (
        <div className="mb-6 bg-white dark:bg-black p-4 font-poppins shadow-md rounded-lg">
          <h2 className="md:text-lg text-md font-semibold mb-4 text-green-600">
            Upload Confirmed Successfully!
          </h2>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
            <p className="text-green-800 dark:text-green-200">
              Your attendance data has been successfully processed and saved to
              the system.
            </p>
            {/* {uploadResponse.message && (
                            <p className="text-green-700 dark:text-green-300 mt-2">{uploadResponse.message}</p>
                        )} */}
            {uploadResponse.insertedCount && (
              <p className="text-green-700 dark:text-green-300 mt-2">
                Number of entries inserted successfully:{" "}
                {uploadResponse.insertedCount}
              </p>
            )}
          </div>
          <div className="flex justify-start items-center">
            <button
              onClick={resetFileSelection}
              className="bg-blue-500 mx-2 hover:bg-blue-600 text-white px-6 py-2 rounded-md"
            >
              Upload Another File
            </button>
            <Link
              to={getRoleBasedRouteLocal(
                "/admin/attendance_management/check-attendance/",
              )}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md"
            >
              Check Uploaded Attendance
              <svg
                className="w-5 h-5"
                data-slot="icon"
                aria-hidden="true"
                fill="none"
                stroke-width="1.5"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceApiPage;
